import { useCallback, useMemo, useState } from "react";

const STORAGE_KEY_PREFIX = "skinny_session_v1_";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isLikelySessionId(value: string): boolean {
  return UUID_RE.test(value.trim());
}

function newSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function storageKey(tenantId: string, siteId: string): string {
  return `${STORAGE_KEY_PREFIX}${tenantId}_${siteId}`;
}

function probeSessionStorage(): boolean {
  if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
    return false;
  }
  try {
    const probe = "__s2r_widget_session_probe__";
    window.sessionStorage.setItem(probe, "1");
    window.sessionStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function readStoredSessionId(key: string): string | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (typeof raw === "string" && isLikelySessionId(raw)) {
      return raw.trim();
    }
  } catch {
    return null;
  }
  return null;
}

function writeStoredSessionId(key: string, sessionId: string): boolean {
  try {
    window.sessionStorage.setItem(key, sessionId);
    return true;
  } catch {
    return false;
  }
}

function removeStoredSessionId(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export interface UseSkinnySessionInput {
  readonly tenantId: string;
  readonly siteId: string;
}

export interface UseSkinnySessionResult {
  readonly sessionId: string;
  readonly clearSession: () => void;
  /** Adopt a server-issued session id (e.g. `X-Session-Id` after rotation). */
  readonly adoptSessionId: (sessionId: string) => void;
  readonly isStateless: boolean;
}

/**
 * Persists a stable `session_id` per tenant/site in `sessionStorage` for widget continuity.
 * Degrades to an in-memory id when storage is unavailable (private mode / blocked APIs).
 */
export function useSkinnySession({ tenantId, siteId }: UseSkinnySessionInput): UseSkinnySessionResult {
  const key = useMemo(() => storageKey(tenantId, siteId), [tenantId, siteId]);

  const [storageAvailable] = useState(() => probeSessionStorage());
  const isStateless = !storageAvailable;

  const [sessionId, setSessionId] = useState<string>(() => {
    if (storageAvailable) {
      const existing = readStoredSessionId(key);
      const id = existing ?? newSessionId();
      if (!existing) {
        writeStoredSessionId(key, id);
      }
      return id;
    }
    return newSessionId();
  });

  const clearSession = useCallback(() => {
    const next = newSessionId();
    removeStoredSessionId(key);
    if (storageAvailable) {
      writeStoredSessionId(key, next);
    }
    setSessionId(next);
  }, [key, storageAvailable]);

  const adoptSessionId = useCallback(
    (nextRaw: string) => {
      const next = nextRaw.trim();
      if (!isLikelySessionId(next)) {
        return;
      }
      setSessionId((prev) => {
        if (next === prev) {
          return prev;
        }
        if (storageAvailable) {
          writeStoredSessionId(key, next);
        }
        return next;
      });
    },
    [key, storageAvailable],
  );

  return {
    sessionId,
    clearSession,
    adoptSessionId,
    isStateless,
  };
}
